import { z } from "zod";
import usePermissions from "../../../../hooks/usePermissions";
import { FEATURES } from "../../../../hooks/usePermissions/permissions";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import useOrganizationIntegrations from "../../hooks/useOrganizationIntegrations";
import {
  CreateIntegrationDocument,
  IntegrationType,
  OrganizationIntegrationsDocument,
} from "../../../../__generated__/graphql";
import Loading from "../../../../components/Loading";
import PimsLogo from "../../../../assets/pims_logo.png";
import { useMutation } from "@apollo/client";
import { useUser } from "../../../../contexts/UserContext/hooks/useUser";
import toast from "react-hot-toast";
import FormInput from "../../../../components/FormInput";
import Button from "../../../../components/Button";
import { useState } from "react";
import Modal from "../../../../components/Modal";

const createDocumentSchema = z.object({
  organization: z.string().min(1, "L'organisation est requise"),
  username: z.string().min(1, "Le nom d'utilisateur est requis"),
  password: z.string().min(1, "Le mot de passe est requis"),
});

type CreateDocumentFormData = z.infer<typeof createDocumentSchema>;

export default function PimsIntegration() {
  const { currentOrganization } = useUser();
  const { can } = usePermissions();

  const [isPimsIntegrationCreated, setIsPimsIntegrationCreated] =
    useState(false);

  const { data: integrations, loading: isLoadingIntegrations } =
    useOrganizationIntegrations();
  const [createIntegration, { loading: isCreatingIntegration }] = useMutation(
    CreateIntegrationDocument
  );

  const pimsIntegration = integrations?.integrations?.find(
    (integration) => integration?.type === IntegrationType.Pims
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateDocumentFormData>({
    resolver: zodResolver(createDocumentSchema),
    defaultValues: {
      organization: pimsIntegration?.config?.organization,
      username: pimsIntegration?.config?.username,
      password: pimsIntegration?.config?.password,
    },
  });

  const onSubmit = async (data: CreateDocumentFormData) => {
    try {
      await createIntegration({
        refetchQueries: [OrganizationIntegrationsDocument],
        variables: {
          createIntegrationInput: {
            type: IntegrationType.Pims,
            config: {
              username: data.username,
              password: data.password,
              organization: data.organization,
            },
            organizationId: currentOrganization?.id ?? "",
          },
        },
      });

      setIsPimsIntegrationCreated(true);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Une erreur est survenue lors de la création de l'intégration"
      );
    }
  };

  if (!can(FEATURES.CREATE_INTEGRATION)) {
    return null;
  }

  if (isLoadingIntegrations) {
    return <Loading />;
  }

  return (
    <>
      {isPimsIntegrationCreated ? (
        <Modal
          isModalOpen={isPimsIntegrationCreated}
          onClose={() => setIsPimsIntegrationCreated(false)}
          title="🎉 Pims est connecté à votre compte Kontrol !"
          description="Vous pourrez facilement importer vos projets, évènements et les informations de billetteries dans Kontrol."
        />
      ) : null}

      <div className="flex flex-col gap-2 bg-dark p-4 rounded-md border border-border w-xl ">
        <div className="text-xl font-bold">Intégrations</div>
        <div className="flex gap-2items-center justify-between">
          <div className="text-primary font-bold text-xl flex gap-2 items-center">
            Pims
            {pimsIntegration ? (
              <div className="text-green-500 bg-green-500/10 rounded-md px-2 py-1 w-fit text-sm">
                Activée
              </div>
            ) : null}
          </div>
          <img src={PimsLogo} alt="pims logo" className="w-20" />
        </div>
        <div className="text-sm text-secondary">
          {pimsIntegration
            ? "Kontrol est synchronisé avec Pims"
            : "Renseignez vos informations pour synchroniser Pims et Kontrol"}
        </div>
        {pimsIntegration ? (
          <div className="flex gap-2 mt-4">
            <p className="text-sm text-primary font-bold">Nom d'utilisateur:</p>
            <div className="text-sm text-secondary">
              {pimsIntegration?.config?.username}
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <FormInput
              id="organization"
              label="Organisation"
              error={errors.organization}
              register={register}
            />
            <FormInput
              id="username"
              label="Nom d'utilisateur"
              error={errors.username}
              register={register}
            />
            <FormInput
              id="password"
              type="password"
              label="Mot de passe"
              error={errors.password}
              register={register}
            />
            <Button
              type="submit"
              className="mt-4 w-full"
              isLoading={isCreatingIntegration}
            >
              Activer
            </Button>
          </form>
        )}
      </div>
    </>
  );
}
